import React, {
  createContext,
  useState,
  useContext,
  useMemo,
  useEffect,
} from "react"

import CssBaseline from "@mui/material/CssBaseline"
import { createTheme, ThemeProvider, ThemeOptions } from "@mui/material/styles"
import useMediaQuery from "@mui/material/useMediaQuery"

import ls, { localKeys } from "@services/localStorage"

import { viewModes } from "@constants/serverEnums"

import { muiAlert } from "./muiAlert"
import { muiAppBar } from "./muiAppBar"
import { muiAutocomplete } from "./muiAutocomplete"
import { muiBackdrop } from "./muiBackdrop"
import { muiButton } from "./muiButton"
import { muiCssBaseline } from "./muiCssBaseline"
import { muiDialog } from "./muiDialog"
import { muiFormControlLabel } from "./muiFormControlLabel"
import { muiIconButton } from "./muiIconButton"
import { muiListItemButton } from "./muiListItemButton"
import { muiMenuItem } from "./muiMenuItem"
import { muiOutlinedInput } from "./muiOutlinedInput"
import { muiSelect } from "./muiSelect"
import { muiTextField } from "./muiTextField"
import { muiToggleButton } from "./muiToggleButton"
import { muiToggleButtonGroup } from "./muiToggleButtonGroup"
import { muiTooltip } from "./muiTooltip"
import { palette } from "./palette"
import { typography } from "./typography"

declare module "@mui/material/styles" {
  interface TypographyVariants {
    caption2: React.CSSProperties
    overline2: React.CSSProperties
  }

  interface TypographyVariantsOptions {
    caption2?: React.CSSProperties
    overline2?: React.CSSProperties
    body1Link?: React.CSSProperties
    body2Link?: React.CSSProperties
    buttonSm?: React.CSSProperties
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    caption2: true
    overline2: true
    body1Link: true
    body2Link: true
    buttonSm: true
  }
}

interface ThemeContextType {
  darkMode: boolean
  toggleDarkMode: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const themeOptions = (darkMode: boolean): ThemeOptions => ({
  components: {
    MuiAlert: muiAlert({ darkMode }),
    MuiAppBar: muiAppBar({ darkMode }),
    MuiAutocomplete: muiAutocomplete(),
    MuiCssBaseline: muiCssBaseline({ darkMode }),
    MuiBackdrop: muiBackdrop(),
    MuiButton: muiButton(),
    MuiDialog: muiDialog(),
    MuiFormControlLabel: muiFormControlLabel(),
    MuiIconButton: muiIconButton({ darkMode }),
    MuiListItemButton: muiListItemButton({ darkMode }),
    MuiMenuItem: muiMenuItem(),
    MuiOutlinedInput: muiOutlinedInput(),
    MuiSelect: muiSelect(),
    MuiTextField: muiTextField(),
    MuiToggleButtonGroup: muiToggleButtonGroup(),
    MuiToggleButton: muiToggleButton(),
    MuiTooltip: muiTooltip(),
  },
  palette: palette({ darkMode }),
  shape: {
    borderRadius: 12,
  },
  typography: typography({ darkMode }),
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useThemeContext: any = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error("useThemeContext must be used within a ThemeProvider")
  }
  return context
}

interface AppThemeProviderProps {
  children: React.ReactNode
}

const AppThemeProvider: React.FC<AppThemeProviderProps> = ({
  children,
}: AppThemeProviderProps) => {
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)")
  // use local storage value if it exists
  // otherwise use user's browser preference
  const lsSavedDark = ls.getItem(localKeys.VIEW_MODE)
    ? ls.getItem(localKeys.VIEW_MODE) === viewModes.DARK
    : prefersDarkMode

  const [darkMode, setDarkMode] = useState(lsSavedDark)

  useEffect(() => {
    setDarkMode(lsSavedDark)
  }, [setDarkMode, lsSavedDark])

  const memoizedValue = useMemo(() => {
    const themeValue: ThemeContextType = {
      darkMode,
      toggleDarkMode: () => {
        setDarkMode((prevMode) => !prevMode)
      },
    }
    return themeValue
  }, [darkMode, setDarkMode])

  const theme = React.useMemo(
    () => createTheme(themeOptions(darkMode)),
    [darkMode],
  )

  return (
    <ThemeContext.Provider value={memoizedValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  )
}

export default AppThemeProvider
